import { useEffect, useMemo, useState } from 'react';
import SideRail from '../components/SideRail.jsx';
import IndividualPerformanceView from '../components/scorecard/IndividualPerformanceView.jsx';
import { getMyScoreCard } from '../services/scoreCardService.js';
import { exportIndividualPerformanceToExcel } from '../utils/scoreCardExport.js';
import {
  SCORE_CARD_PERIOD_OPTIONS,
  formatReportingPeriod,
  resolveScoreCardPeriod,
  toIsoDate,
} from '../utils/scoreCardPeriods.js';

function ScoreCardState({ title, message, action }) {
  return (
    <div className="scorecard-state-card">
      <div className="scorecard-state-card__icon" aria-hidden="true">N3</div>
      <h2>{title}</h2>
      <p>{message}</p>
      {action}
    </div>
  );
}

export default function ScoreCardPage() {
  const today = useMemo(() => new Date(), []);
  const [periodType, setPeriodType] = useState('this-month');
  const initial = useMemo(() => resolveScoreCardPeriod('this-month', today), [today]);
  const [customStart, setCustomStart] = useState(initial.startDate);
  const [customEnd, setCustomEnd] = useState(toIsoDate(today));
  const [scoreCard, setScoreCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const period = useMemo(
    () => resolveScoreCardPeriod(periodType, today, { startDate: customStart, endDate: customEnd }),
    [periodType, today, customStart, customEnd],
  );

  const invalidCustomRange = periodType === 'custom' && (!customStart || !customEnd || customStart > customEnd);

  useEffect(() => {
    if (invalidCustomRange) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');

    getMyScoreCard(period)
      .then((data) => {
        if (!cancelled) setScoreCard(data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('Unable to load Score Card.', { code: err?.code, message: err?.message });
          setScoreCard(null);
          setError('Unable to load Score Card data. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [period.startDate, period.endDate, reloadKey, invalidCustomRange]);

  const individual = scoreCard?.individualPerformance;
  const hasPerformanceData = Boolean(individual && (
    individual.topStats?.length ||
    individual.roleSections?.length ||
    individual.assignments?.length
  ));
  const providerNeedsParityUpdate = Boolean(scoreCard?.status === 'ok' && !hasPerformanceData && (scoreCard?.summary?.projectsDelivered > 0 || scoreCard?.assignments?.length > 0));

  return (
    <div className="app-layout">
      <SideRail />
      <main className="content-page scorecard-page">
        <div className="scorecard-header">
          <div>
            <div className="scorecard-eyebrow">My Performance</div>
            <h1>Score Card</h1>
            {scoreCard?.employee?.name && (
              <div className="scorecard-employee-line">
                <strong>{scoreCard.employee.name}</strong>
                {scoreCard.employee.role && <span>{scoreCard.employee.role}</span>}
                <span>{formatReportingPeriod(scoreCard.period.start, scoreCard.period.end)}</span>
              </div>
            )}
          </div>
          <div className="scorecard-period-controls">
            <label>
              <span>Reporting Period</span>
              <select value={periodType} onChange={(event) => setPeriodType(event.target.value)}>
                {SCORE_CARD_PERIOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            {periodType === 'custom' && (
              <div className="scorecard-custom-range">
                <label><span>Start</span><input type="date" value={customStart} onChange={(event) => setCustomStart(event.target.value)} /></label>
                <label><span>End</span><input type="date" value={customEnd} onChange={(event) => setCustomEnd(event.target.value)} /></label>
              </div>
            )}
          </div>
        </div>

        {invalidCustomRange && <div className="form-error">Choose a valid custom range with the start date on or before the end date.</div>}

        {loading && !invalidCustomRange && (
          <div className="scorecard-skeleton-grid" aria-label="Loading Score Card">
            {Array.from({ length: 9 }).map((_, index) => <div className="scorecard-skeleton" key={index} />)}
          </div>
        )}

        {!loading && error && (
          <ScoreCardState
            title="Unable to load Score Card data"
            message="The performance service could not be reached. No employee data was displayed."
            action={<button type="button" className="text-btn" onClick={() => setReloadKey((value) => value + 1)}>Try again</button>}
          />
        )}

        {!loading && !error && scoreCard?.status === 'setup_required' && (
          <ScoreCardState title="Score Card Setup Required" message={scoreCard.message || 'Complete the Lite Score Card Supabase setup, then refresh this page.'} />
        )}

        {!loading && !error && scoreCard?.status === 'auth_required' && (
          <ScoreCardState title="Score Card Sign-In Required" message={scoreCard.message || 'Please sign out and sign in again so the Score Card can use your authenticated Supabase session.'} />
        )}

        {!loading && !error && scoreCard?.status === 'unmapped' && (
          <ScoreCardState title="Score Card Not Available" message="Your account has not yet been linked to a KPI employee profile. Please contact your administrator." />
        )}

        {!loading && !error && scoreCard?.status === 'integration_unavailable' && (
          <ScoreCardState title="Score Card Integration Required" message={scoreCard.message || 'The secure KPI provider is not fully configured yet.'} />
        )}

        {!loading && !error && providerNeedsParityUpdate && (
          <ScoreCardState title="KPI Provider Update Required" message="The KPI connection is working, but the full Individual Performance provider update has not been installed yet. Run kpi_score_card_provider_v2.sql in the KPI Supabase project." />
        )}

        {!loading && !error && scoreCard?.status === 'ok' && !hasPerformanceData && !providerNeedsParityUpdate && (
          <ScoreCardState title="No performance data" message="No performance data is available for the selected period." />
        )}

        {!loading && !error && scoreCard?.status === 'ok' && hasPerformanceData && (
          <IndividualPerformanceView
            data={individual}
            employeeName={scoreCard.employee.name}
            onExport={() => exportIndividualPerformanceToExcel(individual, scoreCard.employee, scoreCard.period)}
          />
        )}
      </main>
    </div>
  );
}
